/** Sprint 5 · Form Builder · 表單管理列表（建立 / 進 builder / grants / 送審狀態 / 刪除）。 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Chip, IconButton, Paper, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PeopleIcon from '@mui/icons-material/People';
import DeleteIcon from '@mui/icons-material/Delete';
import FactCheckIcon from '@mui/icons-material/FactCheck';

import { usePermission } from '../../../hooks/usePermission';
import { useMessage } from '../../../contexts/MessageContext';
import FunctionPageHeader from '../../../components/FunctionPageHeader';
import { listForms, createForm, deleteForm, FormSummary, FormStatus } from './services/formBuilderService';

const FUNC = 'form_manager';

const STATUS_COLOR: Record<FormStatus, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  draft: 'default', pending_review: 'warning', approved: 'info',
  published: 'success', closed: 'error', deleted: 'default',
};

const FormManagerPage: React.FC = () => {
  const nav = useNavigate();
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`formBuilder.${k}`, d);
  const { hasPermission, loading: permLoading } = usePermission();
  const { showSuccess, showApiError } = useMessage();
  const canRead = hasPermission(FUNC, 'read');
  const canCreate = hasPermission(FUNC, 'create');
  const canDelete = hasPermission(FUNC, 'delete');
  const [items, setItems] = useState<FormSummary[]>([]);

  const load = useCallback(async () => {
    try { setItems((await listForms()).items); } catch (err) { showApiError(err); }
  }, [showApiError]);

  useEffect(() => { if (!permLoading && canRead) load(); }, [permLoading, canRead, load]);

  const create = async () => {
    try {
      const f = await createForm({ title: tt('newFormTitle', '未命名表單'), fields: [] });
      showSuccess('SYS420001');
      nav(`/form_manager/builder/${f.id}`);
    } catch (err) { showApiError(err); }
  };

  const del = async (id: number) => {
    if (!window.confirm(tt('confirmDelete', '確定刪除此表單？（可稽核保留）'))) return;
    try { await deleteForm(id); showSuccess('SYS420001'); load(); } catch (err) { showApiError(err); }
  };

  if (permLoading) return null;
  if (!canRead) return <Box sx={{ p: 3 }}>{t('common.noPermission', '您沒有權限存取此功能')}</Box>;

  return (
    <Box sx={{ p: 2 }}>
      <FunctionPageHeader funcCode={FUNC} />
      <Stack direction="row" spacing={1} sx={{ my: 1.5 }}>
        {canCreate && <Button variant="contained" startIcon={<AddIcon />} onClick={create}>
          {tt('create', '建立表單')}</Button>}
        <Button variant="outlined" startIcon={<FactCheckIcon />} onClick={() => nav('/form_manager/review')}>
          {tt('reviewQueue', '待審清單')}</Button>
      </Stack>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>{tt('title', '標題')}</TableCell>
            <TableCell>{tt('status', '狀態')}</TableCell>
            <TableCell>{tt('targets', '所屬人員')}</TableCell>
            <TableCell align="right">{tt('actions', '操作')}</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {items.length === 0 && (
              <TableRow><TableCell colSpan={4}>
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  {tt('empty', '尚無表單，點「建立表單」開始。')}</Typography>
              </TableCell></TableRow>
            )}
            {items.map((f) => (
              <TableRow key={f.id} hover>
                <TableCell>{f.title}</TableCell>
                <TableCell><Chip size="small" color={STATUS_COLOR[f.status]}
                  label={tt(`st_${f.status}`, f.status)} /></TableCell>
                <TableCell>{(f.target_user_ids || []).length}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" title={tt('edit', '編輯')}
                    onClick={() => nav(`/form_manager/builder/${f.id}`)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" title={tt('grants', '分派')}
                    onClick={() => nav(`/form_manager/${f.id}/grants`)}><PeopleIcon fontSize="small" /></IconButton>
                  {canDelete && <IconButton size="small" color="error" title={tt('del', '刪除')}
                    onClick={() => del(f.id)}><DeleteIcon fontSize="small" /></IconButton>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default FormManagerPage;
